import { useEffect, useState } from "react";
import { Lock, FileText, ArrowLeft, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function NewContract({ onBack, onPick }) {
  const [catalog, setCatalog] = useState(null);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState("All");

  useEffect(() => { api.get("/contracts/catalog").then(({ data }) => setCatalog(data)).catch(() => {}); }, []);
  if (!catalog) return null;

  const cats = ["All", ...catalog.categories];
  const types = catalog.types.filter((t) =>
    (activeCat === "All" || t.category === activeCat) &&
    (!q.trim() || t.name.toLowerCase().includes(q.trim().toLowerCase()) || t.desc.toLowerCase().includes(q.trim().toLowerCase())));

  return (
    <div className="fv-fade-up" data-testid="new-contract">
      <button onClick={onBack} data-testid="new-contract-back" className="flex items-center gap-1.5 text-sm text-[#A1A1AA] hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> Back to contracts</button>
      <h2 className="font-heading text-2xl text-white mb-1">Create a new contract</h2>
      <p className="text-sm text-[#71717A] mb-5">Pick a type — we'll guide you through a short interview and auto-fill everything we already know from your Business Profile.</p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="catalog-search" placeholder="Search agreement types…"
            className="w-full bg-[#121214] border border-white/10 rounded-full pl-9 pr-4 h-10 text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]/50" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-5">
        {cats.map((c) => (
          <button key={c} onClick={() => setActiveCat(c)} data-testid={`catalog-cat-${c}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${activeCat === c ? "fv-gold-gradient text-[#0A0A0B] font-medium" : "bg-white/5 text-[#A1A1AA] hover:text-white"}`}>{c}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalog-grid">
        {types.map((t) => (
          <div key={t.id} data-testid={`catalog-type-${t.id}`}
            className={`fv-card-hover rounded-2xl border p-5 flex flex-col ${t.locked ? "border-white/8 bg-[#0f0f11] opacity-80" : "border-white/8 bg-[#121214]"}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center"><FileText className="w-5 h-5 text-[#D4AF37]" /></div>
              {t.locked ? <span className="flex items-center gap-1 text-[10px] text-amber-300 border border-amber-400/30 rounded-full px-2 py-0.5"><Lock className="w-3 h-3" /> {t.tier}</span>
                : <span className="text-[10px] uppercase tracking-wider text-[#52525B]">{t.category}</span>}
            </div>
            <h3 className="font-heading text-base text-white mb-1">{t.name}</h3>
            <p className="text-xs text-[#71717A] leading-relaxed flex-1 mb-3">{t.desc}</p>
            <Button onClick={() => onPick(t)} disabled={t.locked} data-testid={`catalog-select-${t.id}`}
              className={`w-full rounded-full text-sm ${t.locked ? "bg-white/5 text-[#71717A]" : "fv-gold-gradient text-[#0A0A0B] font-semibold hover:opacity-90"}`}>
              {t.locked ? `Upgrade to ${t.tier}` : "Start"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
